import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CatalogFacility } from '@catalog';

interface Props {
  heading?: ReactNode;
  isLoading: boolean;
  facilities: CatalogFacility[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
}

export default function PreferredFacilitiesField({
  heading,
  isLoading,
  facilities,
  value,
  onChange,
  error,
}: Props) {
  const intl = useIntl();

  const handleSelectAll = () => {
    onChange(facilities.map((f) => f.code));
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  const handleCheckboxChange = (code: string, checked: boolean) => {
    if (checked) {
      onChange([...value, code]);
    } else {
      onChange(value.filter((c) => c !== code));
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        {heading}
        {!isLoading && facilities.length > 0 && (
          <div className="flex gap-1 text-xs font-medium">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-brand-600 hover:text-brand-700"
            >
              {intl.formatMessage({ id: 'accounts.configForm.selectAll' })}
            </button>
            <span className="text-slate-300">/</span>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-brand-600 hover:text-brand-700"
            >
              {intl.formatMessage({ id: 'accounts.configForm.deselectAll' })}
            </button>
          </div>
        )}
      </div>
      {isLoading ? (
        <p className="text-sm text-slate-500">
          <FormattedMessage id="accounts.configForm.loadingFacilities" />
        </p>
      ) : (facilities.length === 0 ? (
        <p className="text-sm text-slate-500">
          <FormattedMessage id="accounts.configForm.noFacilities" />
        </p>
      ) : (
        <div className={`grid gap-2 max-h-48 overflow-y-auto p-2 border rounded-xl bg-white shadow-sm ${error ? 'border-red-300' : 'border-slate-200'}`}>
          {facilities.map((facility) => (
            <label key={facility.code} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value.includes(facility.code)}
                onChange={(e) => handleCheckboxChange(facility.code, e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-200"
              />
              <span className="text-sm text-slate-700">{facility.name}</span>
              {facility.has_asc && (
                <span className="text-xs text-slate-400">
                  <FormattedMessage id="accounts.configForm.facilityTwoInterviews" />
                </span>
              )}
            </label>
          ))}
        </div>
      ))}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
